import { useEffect, useState, useCallback } from "react";
import { EnvelopeSimple, Trash, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { listInquiries, deleteInquiry } from "@/lib/api";

export const InquiriesDialog = ({ open, onOpenChange }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInquiries(await listInquiries());
    } catch (e) {
      toast.error("Could not load inquiries", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const remove = async (id) => {
    try {
      await deleteInquiry(id);
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      toast.success("Inquiry removed");
    } catch (e) {
      toast.error("Could not remove inquiry", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[80vh] overflow-y-auto bg-white border border-ink/15 rounded-none"
        data-testid="inquiries-dialog"
      >
        <DialogHeader>
          <div className="overline text-coral-hover mb-1">Service inbox</div>
          <DialogTitle className="font-display text-3xl tracking-tight">Inquiries received</DialogTitle>
          <DialogDescription className="text-ink-mute">
            People interested in your research support services. Reply directly via email.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center font-mono text-sm text-ink-mute">Loading…</div>
        ) : inquiries.length === 0 ? (
          <div className="py-10 text-center font-mono text-sm text-ink-mute" data-testid="inquiries-empty">
            No inquiries yet. They will appear here once visitors submit the form.
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {inquiries.map((q, i) => (
              <div
                key={q.id}
                className="border border-paper-edge bg-paper-alt p-4"
                data-testid={`inquiry-item-${i}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display text-lg tracking-tight">{q.name}</div>
                    <div className="font-mono text-xs text-ink-mute mt-0.5">
                      {q.email}
                      {q.phone && (
                        <span className="ml-3 inline-flex items-center gap-1">
                          <Phone size={11} weight="bold" /> {q.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider bg-coral text-white">
                    {q.service}
                  </span>
                </div>
                <p className="text-sm text-ink leading-relaxed mt-3">{q.message}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-paper-edge">
                  <span className="font-mono text-[11px] text-ink-mute">
                    {new Date(q.created_at).toLocaleString(undefined, {
                      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                  <div className="flex gap-2">
                    <a
                      href={`mailto:${q.email}?subject=${encodeURIComponent(`Re: ${q.service}`)}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border bg-white border-teal text-teal hover:bg-teal/10 text-[11px] font-mono uppercase tracking-wider"
                      data-testid={`inquiry-reply-btn-${i}`}
                    >
                      <EnvelopeSimple size={12} weight="bold" />
                      Reply
                    </a>
                    <button
                      type="button"
                      onClick={() => remove(q.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border bg-white border-terracotta text-terracotta hover:bg-terracotta/10 text-[11px] font-mono uppercase tracking-wider"
                      data-testid={`inquiry-delete-btn-${i}`}
                    >
                      <Trash size={12} weight="bold" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InquiriesDialog;
